import { useEffect, useMemo, useState } from "react";
import { Info, MoreHorizontal } from "lucide-react";
import { MenuButton } from "~/components/assistant/MenuButton";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import type { PartialSkill } from "~/services/skills";

interface SkillsPopupProps {
  skills: PartialSkill[];
  searchFilter?: string;
  onSelectSkill: (skillName: string) => void;
  onAddSkill?: () => void;
  onEditSkill?: (skill: PartialSkill) => void;
  onDeleteSkill?: (skill: PartialSkill) => void;
  onLearnMore?: () => void;
  enableKeyboardNav?: boolean;
  onClose?: () => void;
  /** Maps dataset scope_id → dataset display name for "from dataset <name>" chips. */
  datasetNames?: Record<string, string>;
}

function filterSkills(skills: PartialSkill[], searchText: string) {
  if (!searchText.trim()) {
    return skills;
  }
  const search = searchText.toLowerCase();
  return skills.filter((skill) => skill.name.toLowerCase().includes(search));
}

function sourceLabel(scope: PartialSkill["scope"]): string {
  switch (scope) {
    case "USER":
      return "from you";
    case "AGENT":
      return "from agent";
    case "DATASET":
      return "from dataset";
    default:
      return scope.toLowerCase();
  }
}

function datasetNameFor(skill: PartialSkill, datasetNames?: Record<string, string>) {
  return skill.scope === "DATASET" && skill.scope_id
    ? (datasetNames?.[skill.scope_id] ?? null)
    : null;
}

export function SkillsPopup({
  skills,
  searchFilter = "",
  onSelectSkill,
  onAddSkill,
  onEditSkill,
  onDeleteSkill,
  onLearnMore,
  enableKeyboardNav = false,
  onClose,
  datasetNames,
}: SkillsPopupProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const filteredSkills = useMemo(
    () => filterSkills(skills, searchFilter).sort((a, b) => a.name.localeCompare(b.name)),
    [skills, searchFilter]
  );

  const allItems = useMemo(() => {
    const items: Array<{ type: "skill" | "action"; skill?: PartialSkill; action?: string }> = [];
    filteredSkills.forEach((skill) => items.push({ type: "skill", skill }));
    items.push({ type: "action", action: "add" });
    items.push({ type: "action", action: "help" });
    return items;
  }, [filteredSkills]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [allItems.length]);

  useEffect(() => {
    if (!enableKeyboardNav) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % allItems.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + allItems.length) % allItems.length);
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        e.stopPropagation();
        const item = allItems[selectedIndex];
        if (item.type === "skill" && item.skill) {
          onSelectSkill(item.skill.name);
        } else if (item.type === "action" && item.action === "add") {
          onAddSkill?.();
          onClose?.();
        } else if (item.type === "action" && item.action === "help") {
          onLearnMore?.();
          onClose?.();
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [enableKeyboardNav, selectedIndex, allItems, onSelectSkill, onClose, onAddSkill, onLearnMore]);

  let currentIndex = 0;

  return (
    <div className="p-2 max-h-[425px] overflow-y-auto">
      {filteredSkills.length > 0 ? (
        <>
          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide px-2 pb-1">
            Skills
          </div>
          {filteredSkills.map((skill) => {
            const itemIndex = currentIndex++;
            const datasetName = datasetNameFor(skill, datasetNames);
            return (
              <div key={skill.id} className="group relative">
                <MenuButton
                  label={
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-900 font-medium">{skill.name}</span>
                        <Badge
                          variant="outline"
                          className="shrink-0 flex items-center gap-1 px-1.5 py-0 text-[0.625rem] uppercase tracking-wide"
                        >
                          {sourceLabel(skill.scope)}
                          {datasetName && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex cursor-default">
                                  <Info className="size-2.5 text-gray-400" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>{datasetName}</TooltipContent>
                            </Tooltip>
                          )}
                        </Badge>
                      </div>
                      <span className="text-xs text-gray-500 line-clamp-2">
                        {skill.description}
                      </span>
                    </div>
                  }
                  onClick={() => onSelectSkill(skill.name)}
                  isSelected={enableKeyboardNav && selectedIndex === itemIndex}
                  rightContent={
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-6 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="size-4 text-gray-600" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-32 p-1" align="end">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full justify-start text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditSkill?.(skill);
                            onClose?.();
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full justify-start text-xs text-red-600 hover:text-red-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteSkill?.(skill);
                            onClose?.();
                          }}
                        >
                          {skill.scope === "USER" ? "Delete" : "Detach"}
                        </Button>
                      </PopoverContent>
                    </Popover>
                  }
                />
              </div>
            );
          })}
        </>
      ) : (
        <div className="px-4 py-3 text-[13px] text-gray-500 text-center">No skills found</div>
      )}

      {filteredSkills.length > 0 && <div className="border-t border-gray-200 my-2" />}

      <MenuButton
        label="Add New Skill..."
        onClick={() => {
          onAddSkill?.();
          onClose?.();
        }}
        isSelected={enableKeyboardNav && selectedIndex === currentIndex++}
      />
      <MenuButton
        label="Learn about AI Skills"
        onClick={() => {
          onLearnMore?.();
          onClose?.();
        }}
        isSelected={enableKeyboardNav && selectedIndex === currentIndex++}
      />
    </div>
  );
}
